import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const OUTCOME_TONE: Record<string, { bg: string; fg: string }> = {
  resolved: { bg: "#e8f4ee", fg: "#1f7a4d" },
  escalated: { bg: "#fbf1dd", fg: "#9a7b1f" },
  voicemail: { bg: "#e8eef7", fg: "#2a5f9e" },
  missed: { bg: "#f7e4e0", fg: "#b3261e" },
};

export function Phone() {
  const { data, isLoading, error } = useQuery({ queryKey: ["phone"], queryFn: api.phone });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" } as const;
  const head = { display: "flex", alignItems: "center", gap: 8, padding: "13px 18px 10px" } as const;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Call & text log</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Voice, SMS, and live chat history. Live calls and texts are also inbox items.
        </div>
      </div>

      {/* Live chats (if any waiting/active) */}
      {data.chats.length > 0 && (
        <div style={{ ...card, boxShadow: "inset 0 0 0 1px rgba(0,103,71,.35)" }}>
          <div style={head}>
            <Icon name="radio" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Live chat</h2>
          </div>
          {data.chats.map((ch) => (
            <div key={ch.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 18px", borderTop: "1px solid #f4f3ee" }}>
              <span style={{ width: 30, height: 30, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 11, flex: "none" }}>{ch.initials}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{ch.visitor} <span style={{ fontSize: 11, color: "#8a8e88", fontWeight: 400 }}>· {ch.role} · {ch.client}</span></div>
                <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{ch.page} · {ch.browser}</div>
              </div>
              {ch.unread > 0 && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#fff", background: "#c0612f", borderRadius: 999, padding: "1px 7px" }}>{ch.unread}</span>}
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: ch.status === "active" ? "#e8f4ee" : "#fbf1dd", color: ch.status === "active" ? "#1f7a4d" : "#9a7b1f" }}>{ch.wait}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr)", gap: 16, alignItems: "start" }}>
        {/* Call log */}
        <div style={card}>
          <div style={head}>
            <Icon name="phone" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Calls</h2>
          </div>
          {data.calls.map((cl) => {
            const tone = OUTCOME_TONE[cl.outcome ?? ""] ?? { bg: "#f0efe9", fg: "#6b706a" };
            return (
              <div key={cl.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 18px", borderTop: "1px solid #f4f3ee" }}>
                <Icon name={cl.direction === "outbound" ? "phone-outgoing" : "phone"} size={14} color={cl.outcome === "missed" ? "#b3261e" : "#8a8e88"} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {cl.who} {cl.ref && <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 10.5, color: "#8a8e88", fontWeight: 400 }}>· {cl.ref}</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{cl.number} · {cl.client} · {cl.when}</div>
                </div>
                {cl.hasRecording && <Icon name="play" size={12} color="#a8aca6" />}
                <span style={{ fontSize: 11, fontFamily: "'Space Mono',monospace", color: "#8a8e88", minWidth: 52, textAlign: "right" }}>{cl.duration}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg, minWidth: 68, textAlign: "center" }}>{cl.outcome}</span>
              </div>
            );
          })}
          {data.calls.length === 0 && <div style={{ padding: "12px 18px", fontSize: 13, color: "#8a8e88", borderTop: "1px solid #f4f3ee" }}>No calls logged.</div>}
        </div>

        {/* SMS threads */}
        <div style={card}>
          <div style={head}>
            <Icon name="message-square" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Text messages</h2>
          </div>
          {data.sms.map((s) => (
            <div key={s.id} style={{ padding: "10px 18px", borderTop: "1px solid #f4f3ee" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{s.who}</span>
                <span style={{ fontSize: 11, color: "#8a8e88", whiteSpace: "nowrap" }}>{s.when}</span>
              </div>
              <div style={{ fontSize: 12, color: "#4a504a", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.lastMessage}</div>
              <div style={{ fontSize: 11, color: "#a8aca6", marginTop: 2 }}>{s.number} · {s.client}{s.ref ? ` · ${s.ref}` : ""}</div>
            </div>
          ))}
          {data.sms.length === 0 && <div style={{ padding: "12px 18px", fontSize: 13, color: "#8a8e88", borderTop: "1px solid #f4f3ee" }}>No text threads.</div>}
        </div>
      </div>
    </Page>
  );
}
